import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["checkbox", "selectAllCheckbox", "row"]
  static values = {
    resourceName: String,
    scopeId: Number,
    ownerType: String
  }

  connect() {
    const scopeId = this.hasScopeIdValue ? this.scopeIdValue : ''
    const ownerType = this.hasOwnerTypeValue ? this.ownerTypeValue : 'org'
    const prefix = scopeId ? `${ownerType}_${scopeId}_` : ''
    this.storageKey = `table_selection_${prefix}${this.resourceNameValue}_ids`

    this.restoreSelections()
    this.setupTableObserver()
    document.addEventListener('turbolinks:load', this.handleTurbolinksLoad)
    document.addEventListener('shown.bs.tab', this.handleTabChange)

    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this))
  }

  handleBeforeUnload = () => {
    this.saveToStorage([])
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
    document.removeEventListener('turbolinks:load', this.handleTurbolinksLoad)
    document.removeEventListener('shown.bs.tab', this.handleTabChange)
    window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this))
  }

  handleTurbolinksLoad = () => {
    this.restoreSelections()
  }

  handleTabChange = () => {
    this.clearAll()
    this.dispatchSelectionChanged()
  }

  setupTableObserver() {
    const tableBody = this.element.querySelector('[data-table-body]')
    if (!tableBody) return

    this.observer = new MutationObserver(() => {
      setTimeout(() => {
        this.restoreSelections()
      }, 50)
    })

    this.observer.observe(tableBody, {
      childList: true,
      subtree: true
    })
  }

  toggleCheckbox(event) {
    const checkbox = event.target
    const id = String(checkbox.value)
    const selectedIds = this.loadFromStorage()

    if (checkbox.checked) {
      if (!selectedIds.includes(id)) {
        selectedIds.push(id)
      }
    } else {
      const index = selectedIds.indexOf(id)
      if (index > -1) {
        selectedIds.splice(index, 1)
      }
    }

    const row = checkbox.closest('tr')
    if (row) {
      if (checkbox.checked) {
        row.classList.add('selected')
      } else {
        row.classList.remove('selected')
      }
    }

    this.saveToStorage(selectedIds)
    this.updateUI()
    this.dispatchSelectionChanged()
  }

  toggleAll(event) {
    const selectAllChecked = event.target.checked
    const selectedIds = this.loadFromStorage()

    this.checkboxTargets.forEach(checkbox => {
      const id = String(checkbox.value)
      checkbox.checked = selectAllChecked

      if (selectAllChecked) {
        if (!selectedIds.includes(id)) {
          selectedIds.push(id)
        }
      } else {
        const index = selectedIds.indexOf(id)
        if (index > -1) {
          selectedIds.splice(index, 1)
        }
      }

      const row = checkbox.closest('tr')
      if (row) {
        if (selectAllChecked) {
          row.classList.add('selected')
        } else {
          row.classList.remove('selected')
        }
      }
    })

    this.saveToStorage(selectedIds)
    this.updateUI()
    this.dispatchSelectionChanged()
  }

  clearAll(event) {
    if (event) event.preventDefault()

    this.saveToStorage([])

    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false

      const row = checkbox.closest('tr')
      if (row) {
        row.classList.remove('selected')
      }
    })

    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.checked = false
    }

    this.updateUI()
    this.dispatchSelectionChanged()
  }

  restoreSelections() {
    const selectedIds = this.loadFromStorage()

    this.checkboxTargets.forEach(checkbox => {
      const id = String(checkbox.value)
      const isSelected = selectedIds.includes(id)
      checkbox.checked = isSelected

      const row = checkbox.closest('tr')
      if (row) {
        if (isSelected) {
          row.classList.add('selected')
        } else {
          row.classList.remove('selected')
        }
      }
    })

    this.updateUI()
  }

  updateUI() {
    if (this.hasSelectAllCheckboxTarget) {
      const visibleCheckboxes = this.checkboxTargets
      const checkedCheckboxes = visibleCheckboxes.filter(cb => cb.checked)

      if (checkedCheckboxes.length === 0) {
        this.selectAllCheckboxTarget.checked = false
        this.selectAllCheckboxTarget.indeterminate = false
      } else if (checkedCheckboxes.length === visibleCheckboxes.length) {
        this.selectAllCheckboxTarget.checked = true
        this.selectAllCheckboxTarget.indeterminate = false
      } else {
        this.selectAllCheckboxTarget.checked = false
        this.selectAllCheckboxTarget.indeterminate = true
      }
    }
  }

  getSelectedIds() {
    return this.loadFromStorage()
  }

  getCount() {
    return this.loadFromStorage().length
  }

  clearSelection() {
    this.clearAll()
  }

  saveToStorage(ids) {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(ids))
    } catch (error) {
      console.error('Failed to save selection to sessionStorage:', error)
    }
  }

  loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.storageKey)
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.error('Failed to load selection from sessionStorage:', error)
      return []
    }
  }

  dispatchSelectionChanged() {
    const event = new CustomEvent('selection:changed', {
      detail: {
        selectedIds: this.getSelectedIds(),
        count: this.getCount(),
        resourceName: this.resourceNameValue
      },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }
}
